import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Camera, Send, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  WeeklyCheckInRow,
  fetchMyWeeklyCheckIns,
  submitWeeklyCheckIn,
  uploadProgressPhoto,
} from '../lib/instantData'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'

const GOAL_STATUS_OPTIONS = ['On track', 'Ahead of schedule', 'Falling behind', 'Need support']

// Monday of the current week, as YYYY-MM-DD, used to label/group check-ins.
function currentWeekStart(): string {
  const now = new Date()
  const day = now.getDay()
  const diff = (day === 0 ? -6 : 1) - day // shift to Monday
  const monday = new Date(now)
  monday.setDate(now.getDate() + diff)
  return monday.toISOString().slice(0, 10)
}

const formatDate = (isoDate: string) => {
  const d = new Date(`${isoDate}T00:00:00`)
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatTimestamp = (createdAt: number) => {
  return new Date(createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const WeeklyProgress: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [history, setHistory] = useState<WeeklyCheckInRow[]>([])
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [programType, setProgramType] = useState<'standard' | 'competition'>('standard')
  const [trainingCompleted, setTrainingCompleted] = useState('')
  const [trainingGoal, setTrainingGoal] = useState('')
  const [goalStatus, setGoalStatus] = useState('')
  const [reflection, setReflection] = useState('')
  const [weight, setWeight] = useState('')
  const [waist, setWaist] = useState('')

  // Each selected photo gets a local-only preview (from the File the user
  // just picked — never fetched over the network) while its upload runs in
  // the background. `path` is set once uploadProgressPhoto() resolves; only
  // photos with a resolved path are included when the check-in is
  // submitted. The app never stores or displays a raw InstantDB signed URL
  // for these.
  interface PendingPhoto {
    localId: string
    previewUrl: string
    path: string | null
    failed: boolean
  }
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([])

  const loadHistory = useCallback(async () => {
    if (!user?.id) return
    try {
      const rows = await fetchMyWeeklyCheckIns(user.id)
      setHistory(rows)
    } catch (err) {
      console.error('Failed to load check-in history:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const alreadySubmittedThisWeek = history.some((h) => h.weekStartDate === currentWeekStart())

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length || !user?.id) return
    setErrorMessage('')

    files.forEach((file) => {
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      const previewUrl = URL.createObjectURL(file)
      setPendingPhotos((prev) => [...prev, { localId, previewUrl, path: null, failed: false }])

      uploadProgressPhoto(user.id, file)
        .then((path) => {
          setPendingPhotos((prev) =>
            prev.map((p) => (p.localId === localId ? { ...p, path } : p))
          )
        })
        .catch((err) => {
          console.error('Failed to upload photo:', err)
          setPendingPhotos((prev) =>
            prev.map((p) => (p.localId === localId ? { ...p, failed: true } : p))
          )
          setErrorMessage('One of your photos failed to upload. You can remove it and try again.')
        })
    })
  }

  const removePhoto = (localId: string) => {
    setPendingPhotos((prev) => {
      const target = prev.find((p) => p.localId === localId)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((p) => p.localId !== localId)
    })
  }

  const photosStillUploading = pendingPhotos.some((p) => !p.path && !p.failed)

  const handleSubmit = async () => {
    if (!user?.id) return
    if (photosStillUploading) {
      setErrorMessage('Please wait for your photos to finish uploading before submitting.')
      return
    }
    setSubmitting(true)
    setErrorMessage('')
    try {
      const measurements =
        programType === 'competition' && (weight.trim() || waist.trim())
          ? {
              ...(weight.trim() ? { weight: weight.trim() } : {}),
              ...(waist.trim() ? { waist: waist.trim() } : {}),
            }
          : undefined

      await submitWeeklyCheckIn({
        userId: user.id,
        programType,
        weekStartDate: currentWeekStart(),
        trainingCompleted: trainingCompleted ? Number(trainingCompleted) : undefined,
        trainingGoal: trainingGoal ? Number(trainingGoal) : undefined,
        goalStatus,
        reflection: reflection.trim(),
        measurements,
        photoPaths: pendingPhotos.filter((p) => p.path).map((p) => p.path as string),
      })
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl))
      setPendingPhotos([])
      setSubmitted(true)
      await loadHistory()
    } catch (err) {
      console.error('Failed to submit check-in:', err)
      setErrorMessage('Something went wrong submitting your check-in. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PanelShell
      eyebrow="Weekly progress"
      title="My Progress"
      subtitle="Submit your weekly check-in and see Cassie's feedback on past weeks."
      actions={
        <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to dashboard
        </GradientButton>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <GlassCard hover={false}>
            {submitted || alreadySubmittedThisWeek ? (
              <div className="text-center py-10">
                <h3 className="font-display text-xl font-semibold text-ink mb-2">
                  This week's check-in is in
                </h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  Cassie will review it and leave feedback here. Come back next week for your next
                  check-in.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-xl font-semibold text-ink mb-1">
                  Week of {formatDate(currentWeekStart())}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Takes a couple of minutes — be honest, this is just for you and Cassie.
                </p>

                <div className="mb-5">
                  <span className="form-label">Program</span>
                  <div className="flex gap-2 mt-1.5">
                    {(['standard', 'competition'] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setProgramType(opt)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                          programType === opt
                            ? 'bg-ink text-white border-ink'
                            : 'border-gray-300 text-gray-600 hover:border-brand'
                        }`}
                      >
                        {opt === 'standard' ? 'Strength Coaching' : 'Competition Prep'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="form-label">Workouts completed</label>
                    <input
                      type="number"
                      min={0}
                      value={trainingCompleted}
                      onChange={(e) => setTrainingCompleted(e.target.value)}
                      className="form-input"
                      placeholder="e.g. 3"
                    />
                  </div>
                  <div>
                    <label className="form-label">Workouts planned</label>
                    <input
                      type="number"
                      min={0}
                      value={trainingGoal}
                      onChange={(e) => setTrainingGoal(e.target.value)}
                      className="form-input"
                      placeholder="e.g. 4"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="form-label">How did this week go overall?</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {GOAL_STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setGoalStatus(opt)}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          goalStatus === opt
                            ? 'bg-brand-soft border-brand text-ink'
                            : 'border-gray-300 text-gray-600 hover:border-brand'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {programType === 'competition' && (
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div>
                      <label className="form-label">Weight (optional)</label>
                      <input
                        type="text"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        className="form-input"
                        placeholder="e.g. 142 lbs"
                      />
                    </div>
                    <div>
                      <label className="form-label">Waist (optional)</label>
                      <input
                        type="text"
                        value={waist}
                        onChange={(e) => setWaist(e.target.value)}
                        className="form-input"
                        placeholder="e.g. 27 in"
                      />
                    </div>
                  </div>
                )}

                <div className="mb-5">
                  <label className="form-label">
                    How was your energy? How did training feel? What went well, and where did you
                    need support?
                  </label>
                  <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Your reflection..."
                  />
                </div>

                <div className="mb-6">
                  <span className="form-label">Progress photos (optional)</span>
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    {pendingPhotos.map((p) => (
                      <div key={p.localId} className="relative w-20 h-20 rounded-xl overflow-hidden group">
                        <img src={p.previewUrl} alt="Progress" className="w-full h-full object-cover" />
                        {!p.path && !p.failed && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          </div>
                        )}
                        {p.failed && (
                          <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center text-white text-[10px] text-center px-1">
                            Failed
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePhoto(p.localId)}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <label className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-brand text-gray-400 hover:text-brand transition-colors">
                      <Camera size={20} />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {photosStillUploading && (
                    <p className="text-xs text-gray-400 mt-2">Uploading...</p>
                  )}
                </div>

                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {errorMessage}
                  </div>
                )}

                <GradientButton
                  onClick={handleSubmit}
                  disabled={submitting || photosStillUploading}
                  icon={Send}
                >
                  {submitting ? 'Submitting...' : 'Submit this week’s check-in'}
                </GradientButton>
              </>
            )}
          </GlassCard>
        </div>

        <div className="lg:col-span-2">
          <GlassCard hover={false}>
            <h3 className="font-display text-lg font-semibold text-ink mb-4">Past check-ins</h3>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500">
                No check-ins yet — submit your first one to start tracking your progress with
                Cassie.
              </p>
            ) : (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {history.map((entry) => (
                  <div key={entry.id} className="p-4 rounded-2xl bg-cream border border-gray-100">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-ink">
                        Week of {formatDate(entry.weekStartDate)}
                      </span>
                      <span className="text-xs text-gray-400">{formatTimestamp(entry.createdAt)}</span>
                    </div>
                    {entry.goalStatus && (
                      <p className="text-xs text-gray-500 mb-2">{entry.goalStatus}</p>
                    )}
                    {entry.coachFeedback ? (
                      <div className="mt-2 p-3 rounded-xl bg-white border border-gray-100">
                        <p className="text-xs font-semibold text-gold-dark mb-1">Cassie's feedback</p>
                        <p className="text-sm text-textPrimary whitespace-pre-wrap">
                          {entry.coachFeedback}
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic mt-2">Waiting on feedback from Cassie</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </PanelShell>
  )
}

export default WeeklyProgress
